<script setup lang="ts">
import {
  tpgIconAdd,
  tpgIconRemoveItem,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { toRefs } from 'vue';

import {
  GrammarQuaternaryButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';

import EditingToolbarView from '#nora-ui/map/components/drawtools/editing/EditingToolbarView.vue';
import type { TextTool } from '#nora-ui/map/drawtools/text-tool';

interface Props {
  mapId: string;
  tool: TextTool;
}

const props = defineProps<Props>();
const { mapId, tool } = toRefs(props);

const { fontSize, text } = storeToRefs(tool.value.textToolStore);
const minimumFontSize = 10;
const maximumFontSize = 32;

function handleSave(enteredText: string): void {
  text.value = enteredText;
  tool.value.onTextUpdated();
}

function updateFontSize(nextFontSize: number): void {
  fontSize.value = Math.min(Math.max(nextFontSize, minimumFontSize), maximumFontSize);
  tool.value.onFontSizeUpdated();
}

function decreaseFontSize(): void {
  updateFontSize(fontSize.value - 1);
}

function increaseFontSize(): void {
  updateFontSize(fontSize.value + 1);
}
</script>

<template>
  <div class="flex flex-col">
    <div class="bg-light-bg-tertiary dark:bg-primary-750 p-2 rounded-md">
      <GrammarTextField
        v-model:value="text"
        label="Text"
        input-type="text"
        placeholder="Enter text"
        @update:value="handleSave"
      />

      <div class="flex flex-row items-center py-1">
        <div class="flex w-full items-center gap-3">
          <GrammarTextField
            class="w-34"
            :value="fontSize.toString()"
            label="Text size"
            :read-only="true"
            input-type="number"
            uom="px"
          />
          <div class="ml-auto flex">
            <GrammarQuaternaryButton
              :disabled="fontSize <= minimumFontSize"
              :icon-template="tpgIconRemoveItem"
              icon-only-size="large"
              @click="decreaseFontSize"
            />
            <GrammarQuaternaryButton
              :disabled="fontSize >= maximumFontSize"
              :icon-template="tpgIconAdd"
              icon-only-size="large"
              @click="increaseFontSize"
            />
          </div>
        </div>
      </div>
    </div>

    <EditingToolbarView
      :draw-tool="tool"
      :map-id="mapId"
    />
  </div>
</template>
